import { pushRoute } from '../../../lib/router.js';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Col, Grid, Row } from 'react-bootstrap';
import { Form } from 'react-final-form';
import SubHeader from '../../../components/SubHeader.jsx';
import { getOrgTermsContent } from '../../../content/utils.js';
import { INITIATIVE_TYPES } from '../../../lib/schema.js';
import { mapFinalFormErrors } from '../../../lib/utils.js';
import {
  retrieveInitiativeCategories,
  selectInitiativeCategoriesWithArchived,
  updateInitiativeCategories,
} from '../../../modules/initiativeCategories.js';
import { validateInitiativeCategories } from '../utils/stakeholdersStructure.js';
import OrgInitiativeCategoriesSettingForm from '../../../components/organizations/Settings/OrgInitiativeCategoriesSettingForm.jsx';
import { selectUserPermissions } from '../../../modules/auth.js';
import { isAuthorized } from '../../../components/AuthorizedAction.js';
import OrganizationHeader from '../../../components/headers/PageHeaders/Organization/OrganizationHeader.jsx';

const mapErrors = mapFinalFormErrors('Failed to save');

const mapStateToProps = (state) => ({
  initiativeCategories: selectInitiativeCategoriesWithArchived(state),
  permissions: selectUserPermissions(state),
});

const mapDispatchToProps = {
  updateInitiativeCategories,
  retrieveInitiativeCategories,
};

const OrgInitiativeCategoriesSettingsFormContainer = (props) => {
  const {
    slug,
    permissions,
    initiativeCategories,
    updateInitiativeCategories,
    retrieveInitiativeCategories,
  } = props;

  const randomId = () => Math.random().toString(36).slice(2, 7);
  const [columns, setColumns] = useState(null);
  const [totalNumberCategories, setTotalNumberCategories] = useState(0);
  const [initial, setInitial] = useState({
    communication: [
      {
        id: null,
        editor: false,
        priority: 0,
        name: '',
        allowDelete: true,
        allowEditor: true,
        draggableId: randomId(),
        tableShow: true,
      },
    ],
    readiness: [
      {
        id: null,
        editor: false,
        priority: 0,
        name: '',
        allowDelete: true,
        allowEditor: true,
        draggableId: randomId(),
        tableShow: true,
      },
    ],
    sustainment: [
      {
        id: null,
        editor: false,
        priority: 0,
        name: '',
        allowDelete: true,
        allowEditor: true,
        draggableId: randomId(),
        tableShow: true,
      },
    ],
    training: [
      {
        id: null,
        editor: false,
        priority: 0,
        name: '',
        allowDelete: true,
        allowEditor: true,
        draggableId: randomId(),
        tableShow: true,
      },
    ],
  });

  const redirectToSettings = () => pushRoute('organization/custom-settings');

  const onSubmit = (values) => {
    const updatedInitiativesData = {};

    Object.keys(columns).forEach((category) => {
      updatedInitiativesData[category] = columns[category].map(
        (initiative, index) => ({
          ...initiative,
          name: initiative.name.replace(/_/g, '-'),
          priority: index,
        })
      );
    });

    const underscoreFilter = () =>
      columns
        .filter((column) => column.name)
        .map((column) => {
          column.name = column.name.replace(/_/g, '-');
          return column;
        });

    const submitPromise = updateInitiativeCategories(updatedInitiativesData);

    return submitPromise
      .then(() => {
        redirectToSettings();
        retrieveInitiativeCategories();
      })
      .catch(mapErrors);
  };

  const validate = (values) => validateInitiativeCategories(values, columns);

  const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result.map((item, index) => ({ ...item, priority: index }));
  };

  const onDragEnd = (result, category) => {
    // dropped outside the list
    if (!result.destination) return;

    const items = reorder(
      columns[category],
      result.source.index,
      result.destination.index
    );

    setColumns((prev) => ({ ...prev, [category]: items }));
  };

  const onCreateColumn = (category) =>
    setColumns((prev) => ({
      ...prev,
      [category]: [
        ...prev[category],
        {
          id: null,
          editor: false,
          priority: columns[category].length,
          name: '',
          allowDelete: true,
          allowEditor: true,
          draggableId: randomId(),
          tableShow: true,
        },
      ],
    }));

  const onDeleteColumn = (category, draggableId) => {
    if (
      window.confirm(
        `${getOrgTermsContent('deleteInitiativeCategory')} \n\n${getOrgTermsContent('deleteInitiativeCategoryDescription')}`
      )
    ) {
      setColumns({
        ...columns,
        [category]: columns[category].filter(
          (column) => column.draggableId !== draggableId
        ),
      });
    }
  };

  const onColumnNameChange = (value, itemIndex, category) => {
    setColumns((prev) => ({
      ...prev,
      [category]: columns[category].map((item, index) =>
        itemIndex === index ? { ...item, name: value } : item
      ),
    }));
  };

  useEffect(() => {
    retrieveInitiativeCategories();
  }, []);

  useEffect(() => {
    if (initiativeCategories) {
      const updatedInitiativesData = {
        communication: [],
        readiness: [],
        sustainment: [],
        training: [],
      };
      INITIATIVE_TYPES.forEach((category, index) => {
        updatedInitiativesData[category] = initiativeCategories
          .filter((c) => !c.archived && c.initiativeType === category)
          .map((initiative) => ({
            ...initiative,
            draggableId: initiative.draggableId || randomId(),
            allowDelete: true,
          }))
          .sort((a, b) => a.priority - b.priority);
      });
      setColumns(updatedInitiativesData);
    }
  }, [initiativeCategories]);

  useEffect(() => {
    if (columns) {
      let totalCountCategories = 0;
      INITIATIVE_TYPES.forEach((category) => {
        if (isAuthorized('update', `${category}Initiative`, permissions)) {
          totalCountCategories += columns[category].filter(
            (i) => !i.archived
          ).length;
        }
      });

      setTotalNumberCategories(totalCountCategories);
    }
  }, [columns]);

  return (
    <React.Fragment>
      <OrganizationHeader slug={slug} />
      <SubHeader flexJustify small>
        <div className="flex">
          <h2>Edit {getOrgTermsContent('initiative')} Categories </h2>
          <div className="flex row m-l-16">
            <strong className="bordered text-muted">
              Total:&nbsp;
              <span className="count">{totalNumberCategories}</span>
            </strong>
          </div>
        </div>
      </SubHeader>

      <Grid>
        <Row>
          <Col sm={12}>
            <div className="component-xl component-bg-white component-xl-padding-vertical component-no-horizontal-padding">
              <Row>
                <Col sm={10} smOffset={1}>
                  {columns ? (
                    <Form
                      component={OrgInitiativeCategoriesSettingForm}
                      onSubmit={onSubmit}
                      validate={validate}
                      onDragEnd={onDragEnd}
                      onCreateColumn={onCreateColumn}
                      onColumnNameChange={onColumnNameChange}
                      onDeleteColumn={onDeleteColumn}
                      initialValues={initial}
                      data={columns}
                      permissions={permissions}
                      slug={slug}
                    />
                  ) : null}
                </Col>
              </Row>
            </div>
          </Col>
        </Row>
      </Grid>
    </React.Fragment>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(OrgInitiativeCategoriesSettingsFormContainer);
